import { lazy, Suspense, useCallback, useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'

import { BackofficeSidebar } from './BackofficeSidebar'
import { Sidebar } from './Sidebar'
import { MobileNavBar } from './MobileNavBar'
import { cn } from '@/lib/utils'
import { CommandPalette, useCommandPalette } from '@/components/layout/CommandPalette'
import { NotificationsProvider } from '@/contexts/NotificationsContext'
import { FeedbackProvider, FeedbackHost } from '@/lib/feedback'
import { WelcomeSequence, useOnboarding } from '@/components/onboarding'
import { PaymentFirstGate } from '@/components/billing/PaymentFirstGate'
import { useAuth } from '@/lib/config/auth-provider'
import { useRouter } from '@/routing/navigation'
import { useSidebarBackGuardControls } from '@/contexts/SidebarBackGuardContext'
import { SidebarExitGuardDialog } from './SidebarExitGuardDialog'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Menu } from '@digital-twins/design-system/icons'
import {
  SIDEBAR_DESKTOP_COLLAPSED_WIDTH,
  SIDEBAR_DESKTOP_EXPANDED_WIDTH,
  SIDEBAR_MOBILE_WIDTH,
} from './sidebar.constants'

// Upgrade modal is only meaningful for trial-like / locked-free users and
// is gated client-side by its own profile read. Lazy-loading keeps the
// chunk (and its billing-API + analytics dependencies) out of the
// critical bundle for paying users.
const UpgradeModalConnected = lazy(() =>
  import('@/components/billing/UpgradeModalConnected').then((m) => ({
    default: m.UpgradeModalConnected,
  })),
)

/** Shared Framer Motion transition for sidebar width changes */
const sidebarTransition = { duration: 0.55, ease: [0.32, 0.72, 0, 1] as const }

interface AppShellMainChromeProps {
  children: React.ReactNode
  mounted: boolean
  isCollapsed: boolean
  toggleSidebar: () => void
  mobileNavOpen: boolean
  setMobileNavOpen: (open: boolean) => void
  isBackofficePage: boolean
  /** Study-creation chrome: the rail is bare until the user expands it. */
  isMinimalChrome?: boolean
  /** Study pane iframe: no sidebar, mobile nav, or command palette. */
  hideAppChrome?: boolean
}

/**
 * Main product chrome (sidebars, command palette, onboarding).
 * Used from Next `AppShell` and Vite `SpaAppShellLayout`.
 */
export function AppShellMainChrome({
  children,
  mounted,
  isCollapsed,
  toggleSidebar,
  mobileNavOpen,
  setMobileNavOpen,
  isBackofficePage,
  isMinimalChrome = false,
  hideAppChrome = false,
}: AppShellMainChromeProps) {
  const { t } = useTranslation('app')
  const router = useRouter()
  const { isBackGuarded } = useSidebarBackGuardControls()
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null)
  const { open: commandPaletteOpen, setOpen: setCommandPaletteOpen } =
    useCommandPalette()
  const { showWelcome, completeWelcome, skipOnboarding } = useOnboarding()
  const { user } = useAuth()
  const isPaymentFirstGated =
    user != null &&
    user.featureFlags?.paidFirstOnboarding === true &&
    !user.hasActiveSubscription

  /**
   * The sidebar is rendered above the router outlet, so a route with unsaved
   * work registers a predicate via `SidebarBackGuardContext` instead of passing
   * dirty state down. When it fires we hold the target and let the confirm
   * dialog decide.
   */
  const handleSidebarNavigateAway = useCallback(
    (to: string) => {
      if (!isBackGuarded()) return false
      setPendingNavigation(to)
      return true
    },
    [isBackGuarded],
  )

  function handleConfirmExit() {
    const to = pendingNavigation
    setPendingNavigation(null)
    if (to) router.push(to)
  }

  if (hideAppChrome) {
    return (
      <FeedbackProvider>
        <NotificationsProvider>
          <div
            className="flex h-dvh min-h-0 w-full flex-col bg-background"
            data-testid="study-embed-shell"
          >
            <main className="flex min-h-0 w-full flex-1 flex-col">{children}</main>
          </div>
        </NotificationsProvider>
      </FeedbackProvider>
    )
  }

  return (
    <FeedbackProvider>
    <NotificationsProvider>
      <div className="flex min-h-screen bg-background">
        <motion.aside
          data-tour="sidebar-nav"
          role="navigation"
          aria-label={
            isBackofficePage ? t('chromeA11y.backofficeNav') : t('chromeA11y.mainNav')
          }
          initial={false}
          animate={{
            width: isCollapsed
              ? SIDEBAR_DESKTOP_COLLAPSED_WIDTH
              : SIDEBAR_DESKTOP_EXPANDED_WIDTH,
          }}
          transition={sidebarTransition}
          className={cn(
            'hidden md:block fixed inset-y-0 left-0 z-40 overflow-hidden',
            isCollapsed && !isBackofficePage ? 'bg-transparent' : 'bg-background',
            !isCollapsed && !isBackofficePage && 'sidebar-expanded-edge',
            // The minimal rail is a bare glyph column: no divider against content.
            !isMinimalChrome && (isBackofficePage
              ? 'border-r border-sidebar-border dark:border-card-border'
              : isCollapsed && 'border-r border-sidebar-border/20 dark:border-card-border/20'),
          )}
        >
          {isBackofficePage ? (
            <BackofficeSidebar
              isCollapsed={isCollapsed}
              toggleSidebar={toggleSidebar}
            />
          ) : (
            // Desktop rail: no `onNavigate`. Clicking a nav item leaves the
            // rail exactly as the user set it (icon+label shell, no
            // collapse-on-click).
            <Sidebar
              isCollapsed={isCollapsed}
              toggleSidebar={toggleSidebar}
              mode={isMinimalChrome ? 'minimal' : undefined}
              onNavigateAway={handleSidebarNavigateAway}
            />
          )}
        </motion.aside>

        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetContent
            side="left"
            className="p-0 bg-background border-sidebar-border overflow-hidden"
            style={{ width: SIDEBAR_MOBILE_WIDTH }}
          >
            <SheetHeader className="sr-only">
              <SheetTitle>{t('chromeA11y.mainNav')}</SheetTitle>
            </SheetHeader>
            {isBackofficePage ? (
              <BackofficeSidebar
                isCollapsed={false}
                toggleSidebar={() => setMobileNavOpen(false)}
              />
            ) : (
              // Mobile sheet: always expanded, and it dismisses itself after a
              // navigation. That used to ride on the nav's collapse-on-click,
              // which the icon+label rail no longer does.
              <Sidebar
                isCollapsed={false}
                toggleSidebar={() => setMobileNavOpen(false)}
                onNavigate={() => setMobileNavOpen(false)}
                onNavigateAway={handleSidebarNavigateAway}
              />
            )}
          </SheetContent>
        </Sheet>

        <div
          style={
            {
              '--desktop-sidebar-width': `${isCollapsed ? SIDEBAR_DESKTOP_COLLAPSED_WIDTH : SIDEBAR_DESKTOP_EXPANDED_WIDTH}px`,
            } as CSSProperties
          }
          className={cn(
            'flex flex-1 flex-col min-w-0 transition-[padding] duration-500',
            'md:pl-[var(--desktop-sidebar-width)]',
          )}
        >
          {/* In-flow when the corner is taken, so the card sits above the page
              instead of covering Survey data, Analyst, or twin chat. Fixed
              (out of flow) on a clear surface. */}
          {!isBackofficePage && <FeedbackHost />}
          {!isBackofficePage && (
            <MobileNavBar onOpenMenu={() => setMobileNavOpen(true)} />
          )}
          {isBackofficePage && (
            <button
              onClick={() => setMobileNavOpen(true)}
              className="md:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-white dark:bg-card hover:bg-accent transition-colors"
              aria-label={t('chromeA11y.openNav')}
            >
              <Menu className="h-5 w-5 text-foreground" strokeWidth={1.5} />
            </button>
          )}
          <main className="flex-1 w-full min-h-0">{children}</main>
        </div>

        {!isBackofficePage && (
          <CommandPalette
            open={commandPaletteOpen}
            onOpenChange={setCommandPaletteOpen}
          />
        )}

        {!isBackofficePage && <PaymentFirstGate />}

        {!isBackofficePage && (
          <AnimatePresence>
            {showWelcome && !isPaymentFirstGated && (
              <motion.div
                key="onboarding-overlay"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0, scale: 1.03 }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
                className="fixed inset-0 z-50"
              >
                <WelcomeSequence
                  onComplete={completeWelcome}
                  onSkip={skipOnboarding}
                />
              </motion.div>
            )}
          </AnimatePresence>
        )}

        {!isBackofficePage && (
          <Suspense fallback={null}>
            <UpgradeModalConnected />
          </Suspense>
        )}

        <SidebarExitGuardDialog
          open={pendingNavigation !== null}
          onOpenChange={(open) => {
            if (!open) setPendingNavigation(null)
          }}
          onConfirm={handleConfirmExit}
        />

      </div>
    </NotificationsProvider>
    </FeedbackProvider>
  )
}
